import dayjs from 'dayjs';
import _ from 'lodash';

import { sortClassTimes } from './sortClassTimes';
import { type ClassSessionGroup } from '../../../context/types';
import { AttendanceType, type CalendarValues, type ClassSession, type ClassSessionWithGroupData } from '../types';

export const calcSessions = (values: CalendarValues) => {
    const {
        days,
        timezone: _timezone,
        dateRange,
        startTime,
        endTime,
        label,
        classSessionGroupUuid,
        selectedGroups,
        attendanceType
    } = values;

    if (!startTime || !endTime) {
        return {
            sessions: [],
            label,
            classSessionGroupUuid,
            isOvernight: false
        };
    }

    let dateStart = dateRange[0];
    let dateEnd = dateRange[1];

    const timezone = _timezone ?? dayjs.tz.guess();

    // Adjusts the date range based on the selected timezone rather than the user's current timezone
    if (dayjs.isDayjs(dateStart) && dayjs.isDayjs(dateEnd)) {
        const sourceTimezoneOffset = dateStart.utcOffset();
        const targetTimezoneOffset = dayjs.tz(dateStart, timezone).utcOffset();
        const timezoneDifference = sourceTimezoneOffset - targetTimezoneOffset;

        dateStart = dateStart.add(timezoneDifference, 'minutes');
        dateStart = dateStart.tz(timezone);

        dateEnd = dateEnd.add(timezoneDifference, 'minutes');
        dateEnd = dateEnd.tz(timezone);
    }

    const startTimeDate = dayjs(startTime, 'HH:mm'); // only used for hours and minutes
    const startHours = startTimeDate.hour();
    const startMins = startTimeDate.minute();

    const endTimeDate = dayjs(endTime, 'HH:mm'); // only used for hours and minutes
    const endHours = endTimeDate.hour();
    const endMins = endTimeDate.minute();

    const a = dayjs.tz(dateStart, 'L', timezone);
    const b = dayjs.tz(dateEnd, 'L', timezone);

    const sessions: ClassSession[] = [];

    for (let m = a; m.diff(b, 'days') <= 0; m = m.add(1, 'days')) {
        if (days?.indexOf(m.day()) !== -1) {
            const sessionStart = m.clone().hour(startHours).minute(startMins);
            const sessionEnd = m.clone().hour(endHours).minute(endMins);

            const shouldAdd24Hours = dayjs(sessionStart).valueOf() > dayjs(sessionEnd).valueOf();
            const sessionEndPlus24 = dayjs(sessionEnd).startOf('minute').add(24, 'hours');

            const session: ClassSessionWithGroupData = {
                start_time: sessionStart.startOf('minute').unix(),
                end_time: shouldAdd24Hours
                    ? sessionEndPlus24.startOf('minute').unix()
                    : sessionEnd.startOf('minute').unix(),
                timezone,
                classSessionGroupUuid,
                label,
                selectedGroups,
                attendanceType
            };

            sessions.push(session);
        }
    }

    const firstSession = sessions[0];
    const isOvernight = firstSession
        ? dayjs.tz(firstSession.start_time * 1000, timezone).get('day') !==
          dayjs.tz(firstSession.end_time * 1000, timezone).get('day')
        : false;

    return {
        sessions,
        label,
        classSessionGroupUuid,
        isOvernight
    };
};

export const calcSessionsFromDates = (dates: CalendarValues[]): ClassSession[] => {
    const allSessions = dates.reduce((previous, date) => {
        const { sessions } = calcSessions(date);

        return [...previous, ...sessions];
    }, [] as ClassSession[]);

    const uniqueSessions = _.uniqBy(allSessions, (session) => {
        const startTimeToNearestMinute = dayjs.unix(session.start_time).startOf('minute').toISOString();
        const endTimeToNearestMinute = dayjs.unix(session.end_time).startOf('minute').toISOString();

        return startTimeToNearestMinute + endTimeToNearestMinute;
    });

    return uniqueSessions.sort((a, b) => dayjs.unix(a.start_time).valueOf() - dayjs.unix(b.start_time).valueOf());
};

interface FilterSessionsInterface {
    sessions: ClassSession[];
    excludedSessions: ClassSession[];
}

export const calcSessionsFromClassTimes = (
    classTimes: string,
    timezone: string,
    classSessionGroups: ClassSessionGroup[]
): ClassSession[] => {
    const classTimesArray = sortClassTimes(classTimes);

    return classTimesArray.map((startAndEndTime) => {
        const [startTime, endTime] = startAndEndTime.split(':');
        const computeAttendanceType = (trackAttendance: boolean, attendanceLmsPush: boolean) => {
            if (trackAttendance) {
                return AttendanceType.NoAttendance;
            }
            return attendanceLmsPush ? AttendanceType.IntegrateWithLms : AttendanceType.Native;
        };

        let classSessionGroupUuid;
        let label;
        let selectedGroups;
        let attendanceType = AttendanceType.Native;
        classSessionGroups.forEach((classSessionGroup) => {
            if (classSessionGroup && classSessionGroup.schedule && classSessionGroup.schedule.class_times) {
                if (classSessionGroup.schedule.class_times.indexOf(startAndEndTime) !== -1) {
                    classSessionGroupUuid = classSessionGroup.class_session_group_uuid;
                    label = classSessionGroup.config?.label;
                    selectedGroups = classSessionGroup.config?.selectedGroups;
                    attendanceType = computeAttendanceType(
                        !!classSessionGroup.config?.trackAttendance,
                        !!classSessionGroup.config?.attendanceLmsPush
                    );
                }
            }
        });

        return {
            start_time: parseInt(startTime, 10),
            end_time: parseInt(endTime, 10),
            timezone,
            classSessionGroupUuid,
            label,
            selectedGroups,
            attendanceType
        };
    });
};

export const filterSessions = ({ sessions, excludedSessions }: FilterSessionsInterface): ClassSession[] =>
    sessions.filter((session) => {
        const found = excludedSessions.find(
            (excludedSession) =>
                excludedSession.start_time === session.start_time && excludedSession.end_time === session.end_time
        );

        return !found;
    });
